import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { DialogContent, Typography } from '@mui/material';

import { GET_CUSTOMER, CREATE_PAYMENT_LINK } from '../../../shared';

import { Loading } from '../../../components/ui';
import DigitalPaymentInfo from './DigitalPaymentInfo';

import {
  StyledForm,
  StyledDigitalPaymentContainerTitle,
  StyledVerticalContent,
  StyledDigitalPaymentV1Container,
  StyledDigitalPaymentV2IFrame,
} from './CustomerDetailsEditor.styled';

const hasApplePay = false;

const loadInlineNovalnetScripts = () => {
  // load Novalnet script
  const script = document.createElement('script');
  script.src = `https://paygate.novalnet.de/v2/checkout-1.0.0.js?t=${new Date().getTime()}`; // Appending a timestamp to avoid caching
  script.integrity = 'sha384-aDPsa/rYHZHxQVB8g9cvUke1bQgiQSaZM6VLi0L1dudq31rn3JG9T6R/0/OHIn9v';
  script.crossOrigin = 'anonymous';
  script.id = 'novalnet-checkout-js';
  document.head.appendChild(script);
};

const setNovalnetParams = (novalnetUrl = '') => {
  const txtSecret = novalnetUrl.split('/nn/')[1]; // txtSecret from the url
  window.Novalnet.setParam('nn_it', 'inline');
  window.Novalnet.setParam('txn_secret', txtSecret);
  window.Novalnet.render('novalnet_payment_form'); // load the digital  payment
};

export default function DigitalPaymentEditor() {
  const { data: customerData } = useQuery(GET_CUSTOMER);
  const { digitalPaymentMethod } = customerData || {};
  const { financialInstrument } = digitalPaymentMethod || {};

  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeLoading, setIframeLoading] = useState(true);
  const [digitalPaymentApiVersion, setDigitalPaymentApiVersion] = useState('V1');

  const [createDigitalPaymentLink] = useMutation(CREATE_PAYMENT_LINK, {
    variables: {
      successUrl: `${window.location.origin}/payment-status/success`,
      failureUrl: `${window.location.origin}/payment-status/fail`,
    },
  });

  useEffect(() => {
    loadInlineNovalnetScripts();

    createDigitalPaymentLink()
      .then((res) => {
        if (res?.data?.createDigitalPaymentLink?.status === 'success') {
          const url = res?.data?.createDigitalPaymentLink?.iframeUrl || '';
          setIframeUrl(url);

          const apiVersion = res?.data?.createDigitalPaymentLink?.digitalPaymentApiVersion || 'V1';
          setDigitalPaymentApiVersion(apiVersion);

          if (apiVersion === 'V1') {
            setNovalnetParams(url);
          }
        }
      })
      .finally(() => {
        setIframeLoading(false);
      });
  }, [createDigitalPaymentLink]);

  return (
    <DialogContent>
      <StyledForm>
        <StyledVerticalContent>
          <Typography variant="h3">
            <FormattedMessage id="customer.details.digital.payment.details.title" />
          </Typography>

          <DigitalPaymentInfo financialInstrument={financialInstrument} />
        </StyledVerticalContent>

        <StyledDigitalPaymentContainerTitle>
          <Typography variant="h3">
            <FormattedMessage id="customer.details.digital.payment.novalnet.form.title" />
          </Typography>
        </StyledDigitalPaymentContainerTitle>

        {iframeLoading && <Loading showLogo />}

        {/* For Digital Payment v1, the Novalnet script will render the iframe */}
        {iframeUrl && digitalPaymentApiVersion === 'V1' && (
          <StyledDigitalPaymentV1Container hasApplePay={hasApplePay} id="novalnet_payment_form" />
        )}

        {/* For Digital Payment v2, the iframe link is directly used */}
        {iframeUrl && digitalPaymentApiVersion === 'V2' && (
          <StyledDigitalPaymentV2IFrame src={iframeUrl} hasApplePay={hasApplePay} />
        )}
      </StyledForm>
    </DialogContent>
  );
}
